import { task } from '@/model/qsadminapi/QsAdminApiModuleModel'
import notify from 'devextreme/ui/notify'
import { useEffect, useRef } from 'react'
import {
  Column,
  DataGrid,
  StateStoring,
  DataGridTypes,
  DataGridRef,
  Pager,
  Toolbar,
  Item,
  FilterRow,
  HeaderFilter,
  ColumnChooser,
  Scrolling,
} from 'devextreme-react/data-grid'
import { RowDblClickEvent } from 'devextreme/ui/data_grid'
import DateColumn from '@/components/date-column/DateColumn'
import { gridTotalItemsLabel } from '@/constants'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { ClickEvent } from 'devextreme/ui/button'
import { TasksToCompleteCardProps } from '@/routes/home/card/Card.types'
import { useScreenSize } from '@/themes/media-query'
import { calculateAusiliariValue, cellRenderAusiliari } from '@/routes/tasks/utils'
import { useQuery } from '@tanstack/react-query'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { HashLoader } from 'react-spinners'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { TaskDashboardRow } from '@/routes/home/card/tech-developer/TasksToCompleteCard.types'

const getAusiliariValue = (rowData: TaskDashboardRow) => calculateAusiliariValue(rowData as unknown as task)

const calculateAusiliariLocalFilterExpression = (
  filterValue: string,
  selectedFilterOperation: string | null,
): string | (() => any) | Array<any> => {
  const currentOperation = selectedFilterOperation ?? 'contains'

  return [(rowData: TaskDashboardRow) => getAusiliariValue(rowData), currentOperation, filterValue]
}

const TasksToCompleteCard = (_props: TasksToCompleteCardProps) => {
  const gridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()
  const { client } = useQsAdminApiManager()
  const { data, isLoading, isRefetching, error, refetch } = useQuery({
    queryKey: ['dashboardOpenTasks'],
    queryFn: async () => {
      const response = await client.get<TaskDashboardRow[]>('/api/task/opentasks')
      return response.data
    },
  })

  useEffect(() => {
    if (!error) return

    notify(
      {
        message: `Errore : ${error instanceof Error ? error.message : 'Impossibile caricare i task dashboard'}`,
        type: 'error',
        displayTime: 5000,
      },
      {
        position: 'bottom center',
        direction: 'up-push',
      },
    )
  }, [error])

  if (isLoading)
    return (
      <div
        style={{
          height: '40vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <HashLoader loading={true} size={100} color={'#ff5722'} />
      </div>
    )

  return (
    <>
      <div style={{ position: 'relative' }}>
        <DataGrid
          id={`dashboard-tasks-to-complete-datagrid`}
          dataSource={data ?? []}
          keyExpr="id"
          ref={gridRef}
          noDataText="Nessun task da completare"
          showBorders={true}
          showColumnLines={true}
          focusedRowEnabled={true}
          columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
          rowAlternationEnabled={true}
          wordWrapEnabled={false}
          repaintChangesOnly={true}
          width="100%"
          height={'40vh'}
          onRowDblClick={(e: RowDblClickEvent<any, any>) => {
            if (e.rowType === 'data') window.open(`/tasks/${e.data.id}`, '_blank')
          }}
          allowColumnResizing={currentScreenSize.isLarge || currentScreenSize.isMedium}
          allowColumnReordering={currentScreenSize.isLarge || currentScreenSize.isMedium}
        >
          <Scrolling mode="virtual"></Scrolling>
          <ColumnChooser enabled={true} />
          <HeaderFilter visible={true}></HeaderFilter>
          <FilterRow visible={true}></FilterRow>
          <Pager
            visible={true}
            allowedPageSizes={'auto'}
            displayMode={'compact'}
            showPageSizeSelector={false}
            showInfo={true}
            showNavigationButtons={false}
            infoText={gridTotalItemsLabel}
          />
          <StateStoring
            enabled={true}
            type="localStorage"
            storageKey={'dashboard-tasks-to-complete-datagrid'}
            savingTimeout={50}
          />
          <Column visible={false} type="buttons" alignment="left" showInColumnChooser={false}></Column>
          <Column dataField={'id'} caption="ID" visible={false} dataType="number" defaultSortOrder="desc"></Column>
          <Column
            dataField="ded_Dis"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const currentTask = cellData.row.data as TaskDashboardRow
              return <ObjectCodeLink url={`/tasks/${currentTask.id}`} text={currentTask.ded_Dis}></ObjectCodeLink>
            }}
            visible={true}
            caption="CODICE"
          ></Column>
          <Column
            dataField="sede.azienda.nome"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const currentTask = cellData.row.data as TaskDashboardRow
              return (
                <ObjectCodeLink
                  url={`/clients/${currentTask.sede?.azienda?.id}`}
                  text={currentTask.sede?.azienda?.nome}
                ></ObjectCodeLink>
              )
            }}
            caption="AZIENDA"
          ></Column>
          <Column
            name={'ausiliari'}
            caption={'AUSILIARI'}
            calculateCellValue={getAusiliariValue}
            calculateDisplayValue={getAusiliariValue}
            calculateSortValue={getAusiliariValue}
            calculateFilterExpression={calculateAusiliariLocalFilterExpression}
            dataType="string"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) =>
              cellRenderAusiliari({
                ...cellData,
                data: cellData.data as task,
              })
            }
            visible={false}
          ></Column>
          {DateColumn({
            dataField: 'apertura',
            caption: 'APERTURA',
            format: 'dd/MM/yyyy',
            width: 'auto',
          })}
          {DateColumn({
            dataField: 'consegna_prevista',
            caption: 'CONSEGNA',
            format: 'dd/MM/yyyy',
            width: 'auto',
          })}
          <Column dataField="proprietario.fullname" caption="AUTORE"></Column>
          <Column dataField="resp_tecnico.fullname" caption="RESPONSABILE" visible={false}></Column>
          <Toolbar>
            <Item
              location="before"
              locateInMenu="never"
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Refresh',
                text: 'Refresh',
                icon: 'refresh',
                onClick: async (_e: ClickEvent) => {
                  await refetch()
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              location="before"
              locateInMenu="never"
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Vai a task',
                text: 'Vai a task',
                icon: 'arrowright',
                onClick: (_e: ClickEvent) => {
                  window.open('/tasks', '_blank')
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              name="columnChooserButton"
              locateInMenu="never"
              showText="inMenu"
              options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
            />
          </Toolbar>
        </DataGrid>
        <LoadingPanel
          visible={isRefetching}
          shading={false}
          position={{ of: '#dashboard-tasks-to-complete-datagrid' }}
        />
      </div>
    </>
  )
}

export default TasksToCompleteCard
